import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

const Parts = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [parts, setParts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const ALL = "All";
  const PARTS_PER_PAGE = 16;

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    category: ALL,
    brands: [],
    models: [],
    price: 2000,
    search: "",
  });

  // ================= TEXT =================
  const t = {
    sq: {
      search: "Kërko pjesë...",
      price: "Çmimi",
      max: "Maksimumi",
      category: "Kategoria",
      brand: "Marka",
      model: "Modeli",
      reset: "Rifillo filtrat",
      parts: "Pjesë",
      view: "Shiko",
      filters: "Filtrat",
      closeFilters: "Mbyll Filtrat",
      noResults: "Duke u ngarkuar...",
    },
    en: {
      search: "Search parts...",
      price: "Price",
      max: "Max",
      category: "Category",
      brand: "Brand",
      model: "Model",
      reset: "Reset filters",
      parts: "Parts",
      view: "View",
      filters: "Filters",
      closeFilters: "Close Filters",
      noResults: "Loading...",
    },
  };

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    const fetchParts = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await fetch(`${API_BASE_URL}/api/parts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const data = await res.json();
        setParts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setParts([]);
      }
    };

    fetchParts();
  }, []);

  // reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, parts]);

  // ================= FILTER LISTS =================
  const categories = useMemo(() => {
    return [ALL, ...new Set(parts.map((p) => p.category).filter(Boolean))];
  }, [parts]);

  const brands = useMemo(() => {
    let base = parts;

    if (filters.category !== ALL) {
      base = base.filter((p) => p.category === filters.category);
    }

    return [...new Set(base.map((p) => p.brand).filter(Boolean))];
  }, [parts, filters.category]);

  const models = useMemo(() => {
    let base = parts;

    if (filters.category !== ALL) {
      base = base.filter((p) => p.category === filters.category);
    }

    if (filters.brands.length > 0) {
      base = base.filter((p) => filters.brands.includes(p.brand));
    }

    return [...new Set(base.map((p) => p.model).filter(Boolean))];
  }, [parts, filters.category, filters.brands]);

  // ================= FILTERED =================
  const filteredParts = useMemo(() => {
    const search = filters.search.toLowerCase();

    return parts.filter((p) => {
      const categoryOk =
        filters.category === ALL || p.category === filters.category;

      const brandOk =
        filters.brands.length === 0 || filters.brands.includes(p.brand);

      const modelOk =
        filters.models.length === 0 || filters.models.includes(p.model);

      const priceOk = Number(p.price || 0) <= filters.price;

      const searchOk =
        !search ||
        p.name?.toLowerCase().includes(search) ||
        p.brand?.toLowerCase().includes(search) ||
        p.model?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search);

      return categoryOk && brandOk && modelOk && priceOk && searchOk;
    });
  }, [parts, filters]);

  // ================= GROUP =================
  const grouped = useMemo(() => {
    const map = new Map();

    filteredParts.forEach((p) => {
      const key = `${p.category}-${p.brand}-${p.model}`;

      if (!map.has(key)) {
        map.set(key, {
          ...p,
          items: [p],
          count: 1,
        });
      } else {
        const existing = map.get(key);
        existing.items.push(p);
        existing.count += 1;
      }
    });

    return Array.from(map.values());
  }, [filteredParts]);

  // ================= PAGINATION =================
  const totalPages = Math.ceil(grouped.length / PARTS_PER_PAGE);

  const paginatedParts = useMemo(() => {
    const start = (currentPage - 1) * PARTS_PER_PAGE;
    return grouped.slice(start, start + PARTS_PER_PAGE);
  }, [grouped, currentPage]);

  // ================= RESET =================
  const resetFilters = () => {
    setFilters({
      category: ALL,
      brands: [],
      models: [],
      price: 2000,
      search: "",
    });
  };

  return (
    <div className="bg-gray-100 min-h-screen py-6 px-3 md:px-4">

      {/* MOBILE FILTER BUTTON */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold"
        >
          {showFilters ? text.closeFilters : text.filters}
        </button>
      </div>

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-4">

        {/* SIDEBAR */}
        <aside className={`
          ${showFilters ? "block" : "hidden"}
          lg:block w-full lg:w-72 bg-white rounded-2xl p-4 shadow-md
          lg:sticky lg:top-6 h-fit
        `}>

          <input
            className="w-full p-2 border rounded mb-4"
            placeholder={text.search}
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
          />

                    {/* PRICE */}
          <div className="mb-5">
            <h3 className="font-semibold mb-2">{text.price}</h3>

            <input
              type="range"
              min="0"
              max="2000"
              value={filters.price}
              onChange={(e) =>
                setFilters({ ...filters, price: Number(e.target.value) })
              }
              className="w-full"
            />

            <div className="flex justify-between text-xs mt-2">
              <span>{text.max}</span>
              <span className="font-bold">
                €{filters.price.toLocaleString()}
              </span>
            </div>
          </div>

          <h3 className="font-semibold mb-2">{text.category}</h3>
          {categories.map((c) => (
            <label key={c} className="block text-sm mb-1">
              <input
                type="radio"
                checked={filters.category === c}
                onChange={() =>
                  setFilters({
                    ...filters,
                    category: c,
                    brands: [],
                    models: [],
                  })
                }
              />
              {" "}{c}
            </label>
          ))}

          <h3 className="font-semibold mt-4 mb-2">{text.brand}</h3>
          {brands.map((b) => (
            <label key={b} className="block text-sm mb-1">
              <input
                type="checkbox"
                checked={filters.brands.includes(b)}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    brands: prev.brands.includes(b)
                      ? prev.brands.filter((x) => x !== b)
                      : [...prev.brands, b],
                  }))
                }
              />
              {" "}{b}
            </label>
          ))}

          <h3 className="font-semibold mt-4 mb-2">{text.model}</h3>
          {models.map((m) => (
            <label key={m} className="block text-sm mb-1">
              <input
                type="checkbox"
                checked={filters.models.includes(m)}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    models: prev.models.includes(m)
                      ? prev.models.filter((x) => x !== m)
                      : [...prev.models, m],
                  }))
                }
              />
              {" "}{m}
            </label>
          ))}

          <button
            onClick={resetFilters}
            className="mt-4 w-full bg-gray-200 py-2 rounded-lg"
          >
            {text.reset}
          </button>
        </aside>

        {/* GRID */}
        <div className="flex-1">

          <h1 className="text-lg md:text-2xl font-bold mb-4">
            {text.parts} ({grouped.length})
          </h1>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">

            {paginatedParts.map((p) => (
              <div
                key={p.id}
                onClick={() => navigate(`/parts/details/${p.items[0].id}`)}
                className="bg-white rounded-xl p-2 md:p-4 shadow hover:shadow-lg transition cursor-pointer"
              >

                <div className="h-24 md:h-32 flex items-center justify-center mb-2">
                  <img
                    src={
                      p.image
                        ? `${API_BASE_URL}${p.image}`
                        : "https://placehold.co/300x300"
                    }
                    className="w-full h-full object-contain"
                    alt={p.name}
                  />
                </div>

                <h3 className="text-xs md:text-sm font-semibold">
                  {p.name}
                </h3>

                <p className="text-xs text-gray-500">
                  {p.category} • {p.brand} • {p.model}
                </p>

                <p className="text-green-600 font-bold mt-2">
                  €{Number(p.price || 0).toFixed(2)}
                </p>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/parts/details/${p.items[0].id}`);
                  }}
                  className="mt-3 w-full bg-green-600 text-white py-2 rounded-lg text-sm"
                >
                  {text.view}
                </button>

              </div>
            ))}

          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8 gap-2 flex-wrap">

              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="px-3 py-2 bg-gray-200 rounded-lg"
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setCurrentPage(num)}
                  className={`px-3 py-2 rounded-lg ${
                    currentPage === num
                      ? "bg-green-600 text-white"
                      : "bg-gray-200"
                  }`}
                >
                  {num}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                className="px-3 py-2 bg-gray-200 rounded-lg"
              >
                Next
              </button>

            </div>
          )}

          {/* EMPTY */}
          {grouped.length === 0 && (
            <div className="bg-white mt-6 p-10 text-center rounded-xl">
              {text.noResults}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Parts;